// THE STAGE'S SCROLL BUS — the one place anything asks "what scrolls here?".
//
// A stage page does not scroll the document. Its content scrolls in its own
// element (components/stage) under the navigator. Everything that used to
// listen to the window (the reading ramp, the fold, the age scrubs, the repo
// rail, the orbit's size) asks this module instead, and it answers with the
// stage's scroller when one stands and with the window when none does. So a
// consumer can move over to the bus on any page, stage or not, and keep working
// on both: that is what lets the stage roll out surface by surface.
//
// Stages STACK: a stage opened over another (a room's reader over the
// dashboard, say) takes the bus while it stands and hands it back when it goes.
// A CONTAINED stage (a preview inside a sheet) never takes it at all.
//
// It also RELAYS input that lands outside the scroller. The navigator and the
// fixed chrome (the crumb bar, the dock, the corners) sit over or beside the
// content, outside its scroll chain, and the document under them no longer
// scrolls: a wheel or a PageDown there would otherwise do nothing. Today it
// scrolled the page; it still does.
//
// No React here: the hooks live in components/stage/useStage.ts.

type Listener = () => void;

const stack: HTMLElement[] = [];
const rebinders = new Set<() => void>();

/** the stage's scroller, or null when the page scrolls the document. */
export function stageScroller(): HTMLElement | null {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].isConnected) return stack[i];
  }
  return null;
}

/** the element (or window) that actually scrolls the page right now. */
export function scrollTarget(): HTMLElement | Window {
  return stageScroller() ?? window;
}

/**
 * Seat `el` as the page's scroller (on top of any stage already standing).
 * Everything subscribed through `onStageScroll` moves over to it at once; the
 * returned function takes it off again and hands the bus to whatever stood
 * before it (the window, when nothing did).
 */
export function registerStageScroller(el: HTMLElement): () => void {
  stack.push(el);
  rebinders.forEach((r) => r());
  armRelays();
  return () => {
    const i = stack.lastIndexOf(el);
    if (i >= 0) stack.splice(i, 1);
    rebinders.forEach((r) => r());
    if (!stack.length) disarmRelays();
  };
}

/** how far the page is scrolled, in px. */
export function stageScrollTop(): number {
  const el = stageScroller();
  if (el) return el.scrollTop;
  return typeof window === "undefined" ? 0 : window.scrollY;
}

/** how far the page CAN scroll, in px (0 when everything fits). */
export function stageScrollMax(): number {
  const el = stageScroller();
  if (el) return Math.max(0, el.scrollHeight - el.clientHeight);
  if (typeof document === "undefined") return 0;
  const d = document.documentElement;
  return Math.max(0, d.scrollHeight - window.innerHeight);
}

const reduced = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** scroll the page to `top`. `instant` unless asked; `smooth` turns instant under reduced motion. */
export function stageScrollTo(top: number, behavior: ScrollBehavior = "instant" as ScrollBehavior): void {
  const b = behavior === "smooth" && reduced() ? ("instant" as ScrollBehavior) : behavior;
  scrollTarget().scrollTo({ top, behavior: b });
}

/** scroll the page by `dy`. */
export function stageScrollBy(dy: number, behavior: ScrollBehavior = "instant" as ScrollBehavior): void {
  const b = behavior === "smooth" && reduced() ? ("instant" as ScrollBehavior) : behavior;
  scrollTarget().scrollBy({ top: dy, behavior: b });
}

/**
 * The box the content is seen through, in viewport px: the scroller's own rect
 * on a stage, the window everywhere else. (The stage's scroller covers the
 * whole stage, so a fraction of this is the same fraction of the screen it
 * always was — a reading line at .38, a view that fills .62.)
 */
export function stageViewport(): { top: number; bottom: number; height: number; left: number; right: number; width: number } {
  const el = stageScroller();
  if (el) {
    const r = el.getBoundingClientRect();
    return { top: r.top, bottom: r.bottom, height: r.height, left: r.left, right: r.right, width: r.width };
  }
  const h = typeof window === "undefined" ? 0 : window.innerHeight;
  const w = typeof window === "undefined" ? 0 : window.innerWidth;
  return { top: 0, bottom: h, height: h, left: 0, right: w, width: w };
}

/** the IntersectionObserver root for content: the scroller, or the viewport (null). */
export function stageIORoot(): HTMLElement | null {
  return stageScroller();
}

/**
 * Hear the page scroll, once per frame at most. Follows the scroller as stages
 * come and go, so a subscriber made before the stage mounted still hears the
 * stage. Returns the unsubscribe.
 */
export function onStageScroll(cb: Listener): () => void {
  let frame = 0;
  let bound: HTMLElement | Window | null = null;
  const fire = () => {
    frame = 0;
    cb();
  };
  const onScroll = () => {
    if (!frame) frame = requestAnimationFrame(fire);
  };
  const bind = () => {
    const next = scrollTarget();
    if (next === bound) return;
    bound?.removeEventListener("scroll", onScroll);
    bound = next;
    bound.addEventListener("scroll", onScroll, { passive: true });
    // the page under the subscriber changed: tell it where it stands now
    onScroll();
  };
  bind();
  rebinders.add(bind);
  return () => {
    rebinders.delete(bind);
    bound?.removeEventListener("scroll", onScroll);
    if (frame) cancelAnimationFrame(frame);
  };
}

/**
 * The scroller an element scrolls inside: the nearest stage / reader / sheet
 * scroller, else the nearest element that actually scrolls, else the page.
 */
export function scrollRootFor(el: Element | null): HTMLElement | Window {
  const marked = el?.closest<HTMLElement>("[data-stage-scroll], [data-art-scroll], [data-sheet-scroll], .eth-scroll");
  if (marked) return marked;
  let n = el?.parentElement ?? null;
  while (n && n !== document.body) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight) return n;
    n = n.parentElement;
  }
  return scrollTarget();
}

// ── the stage's own moves ────────────────────────────────────────────────────
// A scroll the stage makes itself (holding the page still while a split
// navigator folds) is not the reader's movement: the reveal (lib/stage/reveal)
// must not read it as a scroll up, or holding the page would open the head.

const nudges = new WeakMap<HTMLElement, number>();

/** move a scroller without it counting as the reader's scroll */
export function nudgeScroll(el: HTMLElement, top: number): void {
  const before = el.scrollTop;
  el.scrollTop = top;
  const moved = el.scrollTop - before;
  if (moved) nudges.set(el, (nudges.get(el) ?? 0) + moved);
}

/** how far the stage moved this scroller since the last ask (and forget it) */
export function takeNudge(el: HTMLElement): number {
  const n = nudges.get(el) ?? 0;
  nudges.delete(el);
  return n;
}

// ── the relays ───────────────────────────────────────────────────────────────

/** surfaces that own their own keys: a sheet, the menu, a dial, a strip */
const OWNERS = "[data-sheet], [data-gesture-root], .os-fullmenu, .ground-dial.is-open, [data-hscroll], [role=menu], [role=listbox], [role=dialog], .ctx-menu";
/** …and their own wheel. A horizontal strip (the view chips) is NOT one: it
 *  can't take a vertical wheel, and a vertical-dominant wheel is all the relay
 *  ever handles. */
const WHEEL_OWNERS = "[data-sheet], [data-gesture-root], .os-fullmenu, .ground-dial.is-open, [role=menu], [role=listbox], [role=dialog], .ctx-menu";

/** can something between `target` and the page take this vertical scroll itself? */
function innerCanScroll(target: Element | null, dy: number): boolean {
  let n: Element | null = target;
  while (n && n !== document.body && n !== document.documentElement) {
    const el = n as HTMLElement;
    if (el.hasAttribute?.("data-stage-scroll")) return true; // the stage itself: native
    const oy = getComputedStyle(el).overflowY;
    if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1) {
      if (dy > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0) return true;
    }
    n = el.parentElement;
  }
  return false;
}

function locked(): boolean {
  return document.documentElement.hasAttribute("data-scroll-locked");
}

function onWheel(e: WheelEvent) {
  const sc = stageScroller();
  if (!sc || e.ctrlKey || e.defaultPrevented || locked()) return;
  if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
  const t = e.target as Element | null;
  if (!t || (t instanceof Node && sc.contains(t))) return;
  if (t.closest?.(WHEEL_OWNERS)) return;
  if (innerCanScroll(t, e.deltaY)) return;
  const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? sc.clientHeight : 1;
  sc.scrollBy({ top: e.deltaY * k, behavior: "instant" as ScrollBehavior });
}

const PAGE_KEYS = new Set(["PageDown", "PageUp", "ArrowDown", "ArrowUp", "Home", "End", " "]);

function isTyping(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return !!el.closest?.("[contenteditable]:not([contenteditable='false'])");
}

function onKey(e: KeyboardEvent) {
  const sc = stageScroller();
  if (!sc || !PAGE_KEYS.has(e.key) || e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey || locked()) return;
  const t = e.target as Element | null;
  if (isTyping(t)) return;
  if (t && t instanceof Node && sc.contains(t)) return; // native scrolling handles it
  if (t?.closest?.(OWNERS) || t?.closest?.("[role=tablist], [role=slider], [role=radiogroup]")) return;
  // Space on a control presses it; it only pages when nothing is focused
  if (e.key === " " && t && t.closest?.("button, a, [role=button], summary, label")) return;
  const page = Math.max(40, sc.clientHeight - 48);
  const smooth = reduced() ? ("instant" as ScrollBehavior) : "smooth";
  let handled = true;
  switch (e.key) {
    case "PageDown": sc.scrollBy({ top: page, behavior: smooth }); break;
    case "PageUp": sc.scrollBy({ top: -page, behavior: smooth }); break;
    case "ArrowDown": sc.scrollBy({ top: 40, behavior: "instant" as ScrollBehavior }); break;
    case "ArrowUp": sc.scrollBy({ top: -40, behavior: "instant" as ScrollBehavior }); break;
    case "Home": sc.scrollTo({ top: 0, behavior: smooth }); break;
    case "End": sc.scrollTo({ top: sc.scrollHeight, behavior: smooth }); break;
    case " ": sc.scrollBy({ top: e.shiftKey ? -page : page, behavior: smooth }); break;
    default: handled = false;
  }
  if (handled) e.preventDefault();
}

// ── the touch relay ──────────────────────────────────────────────────────────
// A finger that lands on the navigator (its words, its line, its chips) has no
// scroller under it: the navigator lies over the content, outside its scroll
// chain, and the document no longer scrolls. So a vertical drag that starts
// there is carried to the content by hand, with a short glide after it leaves
// the glass. Bound to the navigator element itself (a non-passive touchmove on
// the whole document would make every scroll on the page wait for script).

/** carry vertical drags that start on `head` to the stage's scroller. Returns the unbind. */
export function relayTouches(head: HTMLElement, scroller: () => HTMLElement | null): () => void {
  let y0 = 0;
  let x0 = 0;
  let lastY = 0;
  let lastT = 0;
  let v = 0;
  let mode: "" | "y" | "off" = "";
  let glide = 0;
  const stopGlide = () => { if (glide) cancelAnimationFrame(glide); glide = 0; };
  const start = (e: TouchEvent) => {
    stopGlide();
    const t = e.touches[0];
    if (!t || e.touches.length > 1) { mode = "off"; return; }
    const target = e.target as Element | null;
    // something under the finger that scrolls or owns its own gestures keeps them
    mode = target?.closest?.(WHEEL_OWNERS) ? "off" : "";
    y0 = lastY = t.clientY;
    x0 = t.clientX;
    lastT = e.timeStamp;
    v = 0;
  };
  const move = (e: TouchEvent) => {
    if (mode === "off" || locked()) return;
    const sc = scroller();
    const t = e.touches[0];
    if (!sc || !t) return;
    if (!mode) {
      const dy = t.clientY - y0;
      const dx = t.clientX - x0;
      if (Math.abs(dy) < 6 && Math.abs(dx) < 6) return;
      // sideways is the ticker's and the section swipe's; only vertical is ours
      mode = Math.abs(dy) > Math.abs(dx) * 1.2 ? "y" : "off";
      if (mode === "off") return;
    }
    if (e.cancelable) e.preventDefault();
    const dy = lastY - t.clientY;
    const dt = Math.max(1, e.timeStamp - lastT);
    v = 0.8 * (dy / dt) + 0.2 * v;
    lastY = t.clientY;
    lastT = e.timeStamp;
    sc.scrollTop += dy;
  };
  const end = () => {
    if (mode !== "y") { mode = ""; return; }
    mode = "";
    const sc = scroller();
    if (!sc || reduced()) return;
    // the glide: the drag's own speed, easing out (~friction of a native fling)
    let vel = v * 16;
    const step = () => {
      vel *= 0.94;
      if (Math.abs(vel) < 0.4) { glide = 0; return; }
      sc.scrollTop += vel;
      glide = requestAnimationFrame(step);
    };
    if (Math.abs(vel) > 1) glide = requestAnimationFrame(step);
  };
  head.addEventListener("touchstart", start, { passive: true });
  head.addEventListener("touchmove", move, { passive: false });
  head.addEventListener("touchend", end, { passive: true });
  head.addEventListener("touchcancel", end, { passive: true });
  return () => {
    stopGlide();
    head.removeEventListener("touchstart", start);
    head.removeEventListener("touchmove", move);
    head.removeEventListener("touchend", end);
    head.removeEventListener("touchcancel", end);
  };
}

let armed = false;
function armRelays() {
  if (armed || typeof document === "undefined") return;
  armed = true;
  document.addEventListener("wheel", onWheel, { passive: true });
  document.addEventListener("keydown", onKey);
}
function disarmRelays() {
  if (!armed) return;
  armed = false;
  document.removeEventListener("wheel", onWheel);
  document.removeEventListener("keydown", onKey);
}
